import React, { useEffect, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FaChartBar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PropertyStatsRollup from './PropertyStatsRollup';
import PropertyUnitDrillDown from './PropertyUnitDrillDown';
import { fetchPortfolioStats } from './propertyApi';

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '0';
  }
  return n.toLocaleString('en-IN');
}

function PortfolioKpi({ label, value, onClick, clickable }) {
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      className={`re-portfolio-kpi${clickable ? ' re-portfolio-kpi--clickable' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className='re-portfolio-kpi__value'>{value}</div>
      <div className='re-portfolio-kpi__label'>{label}</div>
    </Tag>
  );
}

function PropertyPortfolioDashboard({ propertyType = 'All', onOpenUnit }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    fetchPortfolioStats({ propertyType, signal: controller.signal })
      .then(data => {
        if (!controller.signal.aborted) {
          setStats(data);
        }
      })
      .catch(err => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Portfolio stats load failed:', err);
        setError(err?.message || 'Failed to load portfolio stats');
        setStats(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [propertyType]);

  useEffect(() => {
    setDrillDown(null);
  }, [propertyType]);

  if (drillDown) {
    return (
      <PropertyUnitDrillDown
        drillDown={drillDown}
        propertyType={propertyType}
        onBack={() => setDrillDown(null)}
        onOpenUnit={onOpenUnit}
      />
    );
  }

  if (loading && !stats) {
    return (
      <div className='re-portfolio re-portfolio--loading'>
        <Spinner animation='border' size='sm' role='status' />
        <span>Loading portfolio…</span>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className='re-portfolio'>
        <Alert variant='danger' className='m-3'>
          {error}
        </Alert>
      </div>
    );
  }

  const data = stats || {
    propertyCount: 0,
    totalUnits: 0,
    occupancyPct: 0,
    activeLeads: 0,
    inventory: [],
    demand: [],
  };

  const typeLabel = propertyType === 'All' ? 'All types' : propertyType;

  return (
    <div className='re-portfolio'>
      <header className='re-portfolio__header'>
        <div>
          <h2 className='re-portfolio__title'>Property portfolio</h2>
          <p className='re-portfolio__subtitle'>
            Portfolio overview · {typeLabel}
            {loading && <span className='re-portfolio__refreshing'> · updating…</span>}
          </p>
        </div>
        <FaChartBar className='re-portfolio__header-icon' aria-hidden='true' />
      </header>

      <div className='re-portfolio__kpis'>
        <PortfolioKpi label='Properties' value={formatCount(data.propertyCount)} />
        <PortfolioKpi label='Inventory' value={formatCount(data.totalUnits)} />
        <PortfolioKpi label='Occupancy' value={`${data.occupancyPct}%`} />
        <PortfolioKpi
          label='Active Leads'
          value={formatCount(data.activeLeads)}
          clickable={data.activeLeads > 0}
          onClick={() => navigate('/listview/property_interest/')}
        />
      </div>

      <PropertyStatsRollup
        stats={data}
        onInventoryClick={setDrillDown}
        onDemandClick={setDrillDown}
      />
    </div>
  );
}

export default PropertyPortfolioDashboard;
